import React, { MouseEvent, ReactElement, useRef } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Button,
  Slide,
  ButtonBase,
} from "@material-ui/core";
import {
  AccountCircle,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
} from "@material-ui/icons/";
import Translator from "../services/Translator";
import { useHistory, Link } from "react-router-dom";
import { useFeature } from "../contexts/FeatureFlags";
import FeatureFlagOptions from "./FeatureFlagOptions";
import { useAuth } from "../contexts/AuthContext";
import ImageUpload from "./ImageUpload";
import { useApp } from "../contexts/AppContext";

const useStyles = makeStyles((theme: Theme) => ({
  header: { zIndex: 100 },
  toolbar: { height: `${theme.goldenRatio(3)}rem`, minHeight: "auto" },
  viInferenceIndicator: {
    content: "",
    width: "100%",
    height: theme.spacing(0.4),
    backgroundColor: theme.palette.secondary.light,
    position: "absolute",
    bottom: 0,
  },
  flexContainer: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    textAlign: "left",
    fontWeight: 400,
  },
  toolbarLogin: {},
  logoButton: {
    marginTop: theme.spacing(0.5),
    marginRight: theme.spacing(2),
  },
  logo: {
    height: `${theme.goldenRatio(1)}rem`,
    paddingBottom: theme.spacing(0.5),
  },
  newCaseButton: {
    marginRight: theme.spacing(4),
  },
}));

export default function MenuBar(): ReactElement {
  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false);
  const viInferenceFeature = useFeature("VI Inference");
  const { isAuthenticated, logout } = useAuth();
  const accountMenuButton = useRef(null);
  const { toggleDrawer, isDrawerOpen } = useApp();
  const history = useHistory();
  const classes = useStyles();

  const isViInference = viInferenceFeature?.active;

  const toggleAccountMenu = () => {
    setAccountMenuOpen((accountMenuOpen) => !accountMenuOpen);
  };

  function handleLogout() {
    logout({ returnTo: window.location.origin });
  }

  const handleToggleMenu = (e: MouseEvent<HTMLElement>) => {
    toggleDrawer && toggleDrawer();
  };

  return (
    <AppBar color="transparent" position="static" className={classes.header}>
      <Toolbar className={!isAuthenticated ? classes.toolbarLogin : classes.toolbar}>
        {isAuthenticated && (
          <div className={classes.flexContainer}>
            <IconButton
              edge="start"
              className={classes.menuButton}
              aria-label="menu"
              onClick={handleToggleMenu}
            >
              {isDrawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
            <ButtonBase
              disableRipple
              disableTouchRipple
              className={classes.logoButton}
              component={Link}
              to="/"
            >
              <img
                className={classes.logo}
                src="/assets/images/Symbol-Color.svg"
                alt={"Solera Logo"}
              />
            </ButtonBase>
            <Typography variant="h6" className={classes.title}>
              VI Core
            </Typography>
            {history.location.pathname.startsWith("/vi") && (
              <ImageUpload noClick>
                <Button
                  size="small"
                  color="secondary"
                  variant="contained"
                  className={classes.newCaseButton}
                  to={"/"}
                  component={Link}
                >
                  {Translator.translate("components.menubar.new_case")}
                </Button>
              </ImageUpload>
            )}
            <IconButton
              ref={accountMenuButton}
              aria-label="account menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={toggleAccountMenu}
            >
              <AccountCircle color="action" />
            </IconButton>
            <Menu
              id="account-menu"
              anchorEl={accountMenuButton.current}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={accountMenuOpen}
              onClose={toggleAccountMenu}
              variant="menu"
            >
              <FeatureFlagOptions onToggleAccountMenu={toggleAccountMenu} />
              <MenuItem id="logout" onClick={handleLogout}>
                {Translator.translate("components.menubar.logout")}
              </MenuItem>
            </Menu>
          </div>
        )}
      </Toolbar>
      <Slide in={isViInference} direction="right">
        <div className={classes.viInferenceIndicator} />
      </Slide>
    </AppBar>
  );
}
